import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { User, ShoppingBag, Heart, Bell, MoreHorizontal, Home, LogOut, MessageSquare, Settings, Image, Users, HelpCircle, Wrench, Megaphone, Palette, Mail, Send, MessageCircle, Search, Bot, Monitor, FileText } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

type TabType = "details" | "orders" | "favorites" | "repairs" | "messages" | "admin" | "settings" | "slider" | "users" | "faq" | "admin-repairs" | "promo-banner" | "color-helper" | "customer-messages" | "sms-templates" | "search-settings" | "bot-settings" | "tv-display" | "blog-management";

interface MobileProfileNavProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  isAdmin: boolean;
  newMessagesCount: number;
  newRepairsCount: number;
  unreadUserMessagesCount: number;
  onLogout: () => void;
  firstName?: string;
}

// Main bottom nav items (4 items + more)
const mainNavItems = [
  { id: "details" as TabType, label: "ראשי", icon: Home },
  { id: "orders" as TabType, label: "הזמנות", icon: ShoppingBag },
  { id: "favorites" as TabType, label: "מועדפים", icon: Heart },
  { id: "repairs" as TabType, label: "מעקב", icon: Bell, hasBadge: true },
];

export const MobileProfileNav = ({
  activeTab,
  onTabChange,
  isAdmin,
  newMessagesCount,
  newRepairsCount,
  unreadUserMessagesCount,
  onLogout,
  firstName,
}: MobileProfileNavProps) => {
  const [moreOpen, setMoreOpen] = useState(false);

  const handleTabClick = (tab: TabType) => {
    onTabChange(tab);
  };

  const handleMoreItemClick = (tab: TabType) => {
    onTabChange(tab);
    setMoreOpen(false);
  };

  // Additional items for "More" drawer
  const moreNavItems = [
    { id: "messages" as TabType, label: "ההודעות שלי", icon: Mail, badge: unreadUserMessagesCount },
  ];

  // Admin items
  const adminNavItems = isAdmin ? [
    { id: "admin" as TabType, label: "הודעות צור קשר", icon: MessageSquare, badge: newMessagesCount },
    { id: "customer-messages" as TabType, label: "שליחת הודעות", icon: Send },
    { id: "admin-repairs" as TabType, label: "ניהול תיקונים", icon: Wrench },
    { id: "users" as TabType, label: "ניהול משתמשים", icon: Users },
    { id: "settings" as TabType, label: "הגדרות תפריטים", icon: Settings },
    { id: "slider" as TabType, label: "ניהול סליידר", icon: Image },
    { id: "faq" as TabType, label: "שאלות נפוצות", icon: HelpCircle },
    { id: "promo-banner" as TabType, label: "פס מבצעים", icon: Megaphone },
    { id: "color-helper" as TabType, label: "עזרה - צבעים", icon: Palette },
    { id: "sms-templates" as TabType, label: "הודעות SMS", icon: MessageCircle },
    { id: "search-settings" as TabType, label: "הגדרות חיפוש", icon: Search },
    { id: "bot-settings" as TabType, label: "הגדרות בוט", icon: Bot },
    { id: "blog-management" as TabType, label: "ניהול בלוג", icon: FileText },
  ] : [];

  const getBadgeCount = (id: TabType) => {
    if (id === "repairs") return newRepairsCount;
    return 0;
  };

  return (
    <>
      {/* Fixed bottom navigation bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border/50 safe-area-pb md:hidden">
        <div className="flex items-center justify-around h-16 px-2">
          {mainNavItems.map((item) => {
            const isActive = activeTab === item.id;
            const badgeCount = getBadgeCount(item.id);
            
            return (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors relative",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.hasBadge && badgeCount > 0 && (
                    <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium">
                      {badgeCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium">{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                  />
                )}
              </button>
            );
          })}

          {/* More button */}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors",
                  moreOpen ? "text-primary" : "text-muted-foreground"
                )}
              >
                <MoreHorizontal className="h-5 w-5" />
                <span className="text-[10px] font-medium">עוד</span>
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] p-0" dir="rtl">
              <div className="flex flex-col h-full">
                {/* Header with greeting */}
                <div className="p-4 border-b border-border/50 bg-muted/30">
                  <p className="text-lg font-light text-foreground text-center">
                    {firstName ? `שלום ${firstName}` : "שלום"}
                  </p>
                </div>

                {/* Menu items */}
                <div className="flex-1 overflow-y-auto p-3 space-y-1">
                  {/* User section */}
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground px-2 mb-2">אפשרויות נוספות</p>
                    {moreNavItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleMoreItemClick(item.id)}
                        className={cn(
                          "flex items-center gap-3 w-full py-2.5 px-3 rounded-lg transition-colors",
                          activeTab === item.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                        )}
                      >
                        <div className="relative">
                          <item.icon className="h-5 w-5" />
                          {item.badge && item.badge > 0 && (
                            <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-sm">{item.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Admin section */}
                  {isAdmin && adminNavItems.length > 0 && (
                    <div className="border-t border-border/50 pt-3">
                      <p className="text-xs text-muted-foreground px-2 mb-2">ניהול האתר</p>
                      {adminNavItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleMoreItemClick(item.id)}
                          className={cn(
                            "flex items-center gap-3 w-full py-2.5 px-3 rounded-lg transition-colors",
                            activeTab === item.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                          )}
                        >
                          <div className="relative">
                            <item.icon className="h-5 w-5" />
                            {item.badge && item.badge > 0 && (
                              <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium">
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <span className="text-sm">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                <div className="p-3 border-t border-border/50 space-y-2">
                  <RouterLink
                    to="/"
                    onClick={() => setMoreOpen(false)}
                    className="flex items-center gap-3 w-full py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <Home className="h-5 w-5 text-foreground" />
                    <span className="text-sm">חזרה לאתר</span>
                  </RouterLink>
                  <button
                    onClick={() => {
                      setMoreOpen(false);
                      setTimeout(onLogout, 100);
                    }}
                    className="flex items-center gap-3 w-full py-2.5 px-3 rounded-lg hover:bg-destructive/10 transition-colors text-destructive"
                  >
                    <LogOut className="h-5 w-5" />
                    <span className="text-sm">התנתק</span>
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind bottom nav */}
      <div className="h-16 md:hidden" />
    </>
  );
};
